// ============================================================
// TMR Portal Admin — Users reads and writes, over @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { authorizationKeys, userKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import type { AccessRoleGrant, ReissueInviteResult } from '../authorization/contract';
import { MIN_LEGACY_SEARCH_LENGTH } from './legacyLink';
import type {
  CreateUserRequest,
  CreateUserResult,
  GrantLegacyAccessRequest,
  GrantLegacyAccessResult,
  LegacyProfile,
  LegacyProfileScreen,
  LegacyUserCandidate,
  UpdateUserRequest,
  UserListItem,
  UserOption,
  UserRecord,
  UserRef,
} from './contract';

/** Filters the Users list accepts; they are part of the cache key, so a change refetches. */
export interface UserListFilters {
  /** Free text matched by the server against name and e-mail. */
  search?: string;
  /** True lists only disabled accounts, false only active ones, omitted both. */
  disabled?: boolean;
  page?: number;
  pageSize?: number;
}

/** How many operators the table shows per page. */
export const USER_PAGE_SIZE = 10;

/**
 * One page of operators, already scoped by the backend to what the caller may see (06 §6).
 *
 * Gated by `users.manage` on the server, so the screen checks the permission before rendering rather than
 * letting every page load 403 (skill `frontend-architecture`, Regra 5).
 */
export function useUserList(filters: UserListFilters = {}): UseQueryResult<Paged<UserListItem>> {
  const query = {
    search: filters.search,
    disabled: filters.disabled,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? USER_PAGE_SIZE,
  };

  return useQuery({
    queryKey: userKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<UserListItem>>('/users', { query, signal }),
    // Keeps the current page on screen while the next loads, so the table does not collapse into a
    // skeleton on every keystroke of the search box.
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads one page of the operator directory **export** (20 §3) — the same filters the table is using.
 *
 * @param filters The screen's active filters.
 */
export function userExportPage(filters: UserListFilters = {}) {
  return exportPageReader<UserListItem>('/users/export', {
    search: filters.search,
    disabled: filters.disabled,
  });
}

/**
 * One operator with their grants (`GET /users/{id}`), for the detail panel.
 *
 * A user outside the caller's scope answers 404 exactly like an unknown id (06 §6) — the two are
 * deliberately indistinguishable, so the screen shows "não encontrado" either way rather than confirming
 * that somebody exists but is none of your business.
 */
export function useUser(userId: string | null): UseQueryResult<UserRecord> {
  return useQuery({
    queryKey: ['user', 'detail', userId ?? ''] as const,
    queryFn: ({ signal }) => api.get<UserRecord>(`/users/${userId}`, { signal }),
    enabled: Boolean(userId),
  });
}

/**
 * Grants a role to an operator (`POST /users/{id}/roles`).
 *
 * Both guards are the server's (06 §5.1): the role's permissions must be a subset of the caller's own,
 * resolved without the cache, and the scope must be within theirs. A refusal comes back as a 403 the screen
 * shows inline — the client never tries to predict it, because predicting it wrong in the permissive
 * direction is how an escalation path opens.
 */
export function useAssignRole(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (grant: AccessRoleGrant) =>
      api.post<{ assignmentId: string }>(`/users/${userId}/roles`, grant),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/** Revokes one grant (`DELETE /users/{id}/roles/{assignmentId}`), targeting the assignment, not the role. */
export function useRevokeRole(userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (assignmentId: string) =>
      api.delete<void>(`/users/${userId}/roles/${assignmentId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Creates an operator and invites them into Clerk (06 §6.1).
 *
 * The result's `inviteSent` is the part callers must not ignore: a queued invitation still created the
 * account, so this resolves successfully either way, and it is the screen's job to say which happened.
 * Treating a queued invitation as plain success is exactly the silence this whole slice set out to fix.
 */
export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateUserRequest) => api.post<CreateUserResult>('/users', request),
    onSuccess: () => {
      // The new operator belongs on every list, whatever filter is showing.
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * "Reenviar convite" from the Users screen: revokes the person's invitation and sends a new one (06 §6.1).
 * Same rule as the Acessos screen, reached by user instead of by request — so both screens' caches go.
 */
export function useReissueUserInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => api.post<ReissueInviteResult>(`/users/${userId}/reissue-invite`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      void queryClient.invalidateQueries({ queryKey: authorizationKeys.all });
    },
  });
}

/** Edits an operator's name and phone. The e-mail is immutable (05 §8), so it is not here. */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...request }: UpdateUserRequest & { id: string }) =>
      api.put<void>(`/users/${id}`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/** How many operators a picker shows — enough to choose from, never a report. */
const PICKER_PAGE_SIZE = 8;

/** Below this many characters a search is not worth a round-trip, so the picker stays idle. */
const MIN_SEARCH_LENGTH = 2;

/**
 * Operators matching a term, for the pickers that name one — today the audit
 * log's actor filter (10 B5). The Empresa's manager fields read `/companies/managers` instead (18 §3).
 *
 * The same list endpoint as the table, asked in the shape a picker needs: a short page and just the items.
 * Gated by `users.manage` on the server, so the caller must hide the picker without it (skill
 * `frontend-architecture`, Regra 5).
 *
 * @param term Free text matched by the server against name and e-mail.
 */
export function useUserSearch(term: string): UseQueryResult<UserOption[]> {
  const trimmed = term.trim();
  const query = { search: trimmed, page: 1, pageSize: PICKER_PAGE_SIZE };

  return useQuery({
    queryKey: userKeys.search(query),
    queryFn: ({ signal }) => api.get<Paged<UserOption>>('/users', { query, signal }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    select: (page) => page.items,
  });
}

/**
 * One operator's name, for screens that only hold their id — today, the authorization audit log (10 B7c).
 *
 * Gated by `users.manage` on the server, so the caller passes `enabled: false` without it and shows the
 * raw id instead: a name the operator may not see is not worth a 403 per row. A user outside their scope
 * answers 404 exactly like an unknown id (06 §6), which lands on the same fallback.
 *
 * @param userId The account to name, or null when there is no actor to resolve.
 * @param enabled Pass false to skip the request entirely.
 */
export function useUserRef(userId: string | null, enabled: boolean): UseQueryResult<UserRef> {
  return useQuery({
    queryKey: ['user', userId ?? ''] as const,
    queryFn: ({ signal }) => api.get<UserRef>(`/users/${userId}`, { signal }),
    enabled: enabled && Boolean(userId),
    // A person's name does not change while the operator reads a page of the log.
    staleTime: 5 * 60_000,
  });
}

/**
 * Legacy staff logins matching a term, for the modal that correlates an account to V1 by hand (05 I7).
 *
 * The endpoint exists only where a legacy connection is configured, so a 404 here means "not available in
 * this environment" rather than "nothing found" — the modal has to say those differently. Callers that
 * want the automatic attempt pass the account's own e-mail as the term.
 *
 * @param term Free text matched by the server against the legacy name, e-mail, login and id.
 */
export function useLegacyUserSearch(term: string): UseQueryResult<LegacyUserCandidate[]> {
  const trimmed = term.trim();

  return useQuery({
    queryKey: userKeys.legacySearch(trimmed),
    queryFn: ({ signal }) =>
      api.get<LegacyUserCandidate[]>('/users/legacy', { query: { search: trimmed }, signal }),
    enabled: trimmed.length >= MIN_LEGACY_SEARCH_LENGTH,
  });
}

/**
 * Correlates an account to a legacy login (`POST /users/{id}/legacy-link`).
 *
 * Invalidates the whole user root, the picker included: the legacy id is unique, so taking one changes
 * what every other open search may offer.
 */
export function useLinkLegacyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, legacyUserId }: { id: string; legacyUserId: number }) =>
      api.post<void>(`/users/${id}/legacy-link`, { legacyUserId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * The ScriptCase staff profiles the Portal Interno login can carry (`GET /users/legacy-profiles`, 33).
 *
 * Reference data read from the legacy, so it is kept for a while: a profile's template changes in the
 * ScriptCase, never from here.
 *
 * @param enabled Pass false until the modal opens, so the list is not read for every visit to Usuários.
 */
export function useLegacyProfiles(enabled: boolean): UseQueryResult<LegacyProfile[]> {
  return useQuery({
    queryKey: userKeys.legacyProfiles(),
    queryFn: ({ signal }) => api.get<LegacyProfile[]>('/users/legacy-profiles', { signal }),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The screens one profile opens (`GET /users/legacy-profiles/{id}/screens`) — exactly what the login will get.
 *
 * @param profileId The chosen profile, or null before one is chosen.
 */
export function useLegacyProfileScreens(profileId: number | null): UseQueryResult<LegacyProfileScreen[]> {
  return useQuery({
    queryKey: userKeys.legacyProfileScreens(profileId ?? 0),
    queryFn: ({ signal }) =>
      api.get<LegacyProfileScreen[]>(`/users/legacy-profiles/${profileId}/screens`, { signal }),
    enabled: profileId !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * Creates the account's staff login in the Portal Interno and links it (`POST /users/{id}/legacy-access`, 33).
 *
 * Invalidates the whole user root: the row gains its legacy id, the profiles' head counts change, and the
 * link picker must no longer offer the new login as free.
 */
export function useGrantLegacyAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: GrantLegacyAccessRequest & { id: string }) =>
      api.post<GrantLegacyAccessResult>(`/users/${id}/legacy-access`, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Drops an account's legacy correlation (`DELETE /users/{id}/legacy-link`) — how a wrong link is fixed,
 * since its rightful owner cannot take the id while this account holds it.
 */
export function useUnlinkLegacyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/users/${id}/legacy-link`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

/**
 * Turns an account's kill switch on or off (05 I3).
 *
 * Disabling preserves the record — it is not a delete — which is why the screen offers it instead, and why
 * reactivating is a single call rather than re-creating the person.
 */
export function useSetUserDisabled() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, disabled }: { id: string; disabled: boolean }) =>
      api.post<void>(`/admin/users/${id}/${disabled ? 'disable' : 'enable'}`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
